import {
    getBrandCategories,
    getNewHomeGrownBrands,
    getProductBrands,
} from '@api/brand';
import { getHomegrownProducts } from '@api/product';
import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Box,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
} from '@chakra-ui/react';
import { Container, Header, Section } from '@components';
import {
    BrandCard,
    RichCard,
} from '@components/cards';
import { SearchContainer } from '@components/container';
import { Footer } from '@components/footer';
import { TBrand, TProduct, TProductCategory } from '@components/types';
import appConfig from '@core/appconfig';
import { sanityIoImageLoader } from '@core/utils';
import { Checkbox } from 'antd';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import React, { Suspense, useEffect, useState } from 'react';

interface Props {
  initialGiftingBrand?: TBrand[];
  initialBrands?: TBrand[];
  initialProducts?: TProduct[];
  initialOrganicBrands?: TBrand[];
  initialFashionBrands?: TBrand[];
}

export const getServerSideProps: GetServerSideProps<{
  initialGiftingBrand: TBrand[];
  initialProducts: TProduct[];
  initialOrganicBrands: TBrand[];
  initialFashionBrands: TBrand[];
}> = async (context) => {
  const [
    initialProducts,
    initialBrands,
    initialGiftingBrand,
    initialOrganicBrands,
    initialFashionBrands,
  ] = await Promise.all([
    getHomegrownProducts(),
    getNewHomeGrownBrands(),
    getProductBrands(null, 'gift', 0, 5),
    getProductBrands(null, 'organic', 0, 5),
    getProductBrands(null, 'fashion', 0, 5),
  ]);

  return {
    props: {
      initialProducts: initialProducts,
      initialBrands,
      initialGiftingBrand,
      initialOrganicBrands,
      initialFashionBrands,
    },
  };
};

const Navbar = dynamic(
  () => import('../../src/components/navbar').then((mod) => mod.Navbar),
  {
    ssr: false,
  }
);

const HomeGroundBrandPage: React.FC = ({
  initialProducts,
  initialBrands,
  initialGiftingBrand,
  initialOrganicBrands,
  initialFashionBrands,
}: Props) => {
  const [homegrownProducts, setHomegrownProducts] =
    useState<TProduct[]>(initialProducts);
  const [brandCategories, setBrandCategories] = useState<TProductCategory[]>();
  const [popularBrands, setPopularBrands] = useState<TBrand[]>(initialBrands);
  const [giftingBrands, setGiftingBrands] =
    useState<TBrand[]>(initialGiftingBrand);
  const [organicBrands, setOrganicBrands] =
    useState<TBrand[]>(initialOrganicBrands);
  const [fashionBrands, setFashionBrands] =
    useState<TBrand[]>(initialFashionBrands);
  const pageSize = 20;

  const fetchHomegrownProducts = async () => {
    const products = await getHomegrownProducts();
    setHomegrownProducts(products);
  };

  const getAllBrandCategories = () => {
    getBrandCategories(null, 0, pageSize).then((res) => {
      setBrandCategories(res);
    });
  };

  useEffect(() => {
    getAllBrandCategories();
    fetchHomegrownProducts();
  }, []);

  return (
    <Container>
      <Head>
        <title>Homegrown Brands - {appConfig.appName}</title>
        <meta
          name="Keywords"
          content={`Homegrown brands,Solopreneur brands,Local Products,Local Services`}
        />
        <meta
          name="Description"
          content={`Find Homegrown, Local, Solopreneur brands and products on ${appConfig.appName}`}
        />
        <meta property="og:title" content={`Homegrown Brands - ${appConfig.appName}`} />
        <meta
          name="og:description"
          content={`Find Homegrown, Local, Solopreneur brands and products on ${appConfig.appName}`}
        />
        <meta
          name="og:image"
          content={`https://jhattse.com/api/v1/file/?key=esangrah/48XPIZHA_brandhomeground.png`}
        />
      </Head>
      <Header />
      <div className="pb-20 max-w-[1240px] w-full mx-auto">
        <div className="hidden py-8 md:flex items-center text-base font-manrope">
          <Breadcrumb className="text-base text-neutral-400 font-medium font-manrope list-none text-breadcrumbs">
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbLink href="/brand/explore">Brand</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem
              className="text-ellipsis text-left break-words line-clamp-1 select-none text-neutral-800 font-semibold"
              isCurrentPage
            >
              <BreadcrumbLink href={`/brand/homegrown`}>
                Homegrown Brands
              </BreadcrumbLink>
              <ol className="p-0 list-none"></ol>
            </BreadcrumbItem>
          </Breadcrumb>
        </div>
        <div className="flex flex-col gap-8 px-2 lt-sm:mt-5 font-manrope">
          {/* Brand Banner */}
          <div className="w-full">
            <h1 className="text-lg font-extrabold text-neutral-700 pb-2">
              Homegrown Brands
            </h1>
            <Image
              loading='eager'
              loader={sanityIoImageLoader}
              src={
                'https://jhattse.com/api/v1/file/?key=esangrah/48XPIZHA_brandhomeground.png'
              }
              alt={`Homegrown brands on ${appConfig?.appName}`}
              width="1200"
              height="300"
              quality={100}
              className="w-full h-full max-h-96 lt-sm:max-h-40 object-cover md:rounded-2xl rounded-lg"
            />
          </div>
          {/* Tabs */}
          <div className="flex flex-col gap-2">
            <div className="font-manrope font-extrabold font-manrope md:text-lg text-base text-neutral-700">
              Top Brands in Trending Categories
            </div>
            <div className="md:grid md:grid-cols-3 flex flex-col gap-4">
              <div className="bg-gradient-to-r from-new_lunch-200 to-new_lunch-100 rounded-lg p-4 flex flex-col gap-2 justify-between">
                <Suspense>
                  <Section
                    title="Gifting"
                    products={giftingBrands}
                    element={BrandCard}
                    isCarousel={true}
                    style="text-neutral-900"
                    headStyle="md:text-xl text-lg py-1 font-semibold font-manrope"
                  />
                </Suspense>
              </div>
              <div className="bg-gradient-to-r from-green-200 to-green-100 rounded-lg p-4 flex flex-col gap-2 justify-between">
                <Suspense>
                  <Section
                    title="Organic Foods"
                    products={organicBrands}
                    element={BrandCard}
                    isCarousel={true}
                    style="text-neutral-900"
                    headStyle="md:text-xl text-lg py-1 font-semibold font-manrope"
                  />
                </Suspense>
              </div>
              <div className="bg-gradient-to-r from-red-300 to-red-100 rounded-lg p-4 flex flex-col gap-2 justify-between">
                <Suspense>
                  <Section
                    title="Fashion and Apparel"
                    products={fashionBrands}
                    element={BrandCard}
                    isCarousel={true}
                    style="text-neutral-900"
                    headStyle="md:text-xl text-lg py-1 font-semibold font-manrope"
                  />
                </Suspense>
                <Link href={'/brand/homegrown'} prefetch={false}>
                  <div className="w-full flex items-center font-manrope">
                    <button className="font-bold md:text-base text-sm text-brand-500 font-manrope">
                      VIEW ALL
                    </button>
                  </div>
                </Link>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <Suspense>
              <Section
                title="Homegrown Products"
                products={homegrownProducts}
                element={RichCard}
                isCarousel={true}
                style="text-neutral-900"
                headStyle="text-lg py-1 font-extrabold font-manrope"
              />
            </Suspense>
          </div>
          <div className="flex flex-col">
            <div className="md:grid grid-cols-6 divide-x divide-solid divide-y-none divide-neutral-400 border-b border-b-solid border-neutral-400 hidden">
              <div className="flex items-center justify-between col-span-1 pr-4 border-r border-r-solid border-neutral-400">
                <div className="text-base font-bold text-neutral-700 font-manrope">
                  Filters
                </div>
                <span className="text-sm font-bold text-brand-500 font-manrope cursor-pointer whitespace-nowrap">
                  Clear All
                </span>
              </div>
            </div>
            <div className="md:grid flex flex-col grid-cols-6 divide-x md:divide-solid divide-y-none divide-neutral-400">
              <div className="md:flex flex-col items-start gap-4 flex-1 col-span-1 hidden py-2">
                <Accordion
                  defaultIndex={0}
                  allowToggle
                  p={0}
                  className="w-full"
                >
                  <AccordionItem>
                    <h2>
                      <AccordionButton p={0} pr={20}>
                        <Box as="span" flex="1" textAlign="left">
                          <span className="text-base font-bold text-neutral-700 font-manrope">
                            Category
                          </span>
                        </Box>
                        <AccordionIcon fontSize={20} fontWeight={700} />
                      </AccordionButton>
                    </h2>
                    <AccordionPanel pb={4}>
                      <div className="flex flex-col gap-2 py-4">
                        {brandCategories?.length > 0 &&
                          brandCategories?.map((brandCat) => (
                            <Checkbox className="text-base font-medium text-neutral-500 font-manrope">
                              {brandCat?.name}
                            </Checkbox>
                          ))}
                      </div>
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="flex-3 col-span-5 md:pl-4 px-0">
                <SearchContainer
                  noOfCols="xl:grid-cols-5 md:grid-cols-4 grid-cols-2"
                  searchresults={popularBrands}
                  element={BrandCard}
                  isLoadMore={false}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Navbar />
      <Footer />
    </Container>
  );
};

export default HomeGroundBrandPage;
